<template>
  <HotkeyCapture :value="hotkey" @update:value="onUpdate" />
</template>

<script lang="ts">
/**
 * Capture field for an Electron OS-global shortcut.
 *
 * Wraps the pure HotkeyCapture field and owns the registration side: the
 * hotkey named by `hotkey-name` is loaded from, and persisted to, the main
 * process's global shortcut system through `hotkeys.ipc` (see
 * src/utils/hotkeys.ts). Desktop only — the settings screens using it fall
 * back to a "desktop app" notice on web.
 */

import { defineComponent, onMounted, ref } from "vue";

import HotkeyCapture from "@/components/HotkeyCapture.vue";
import { metrics } from "@/metrics";
import { hotkeys, type Hotkey } from "@/utils/hotkeys";
import { in_electron } from "@/utils/in";
import { keymap } from "@/utils/keymap";

export default defineComponent({
  components: {
    HotkeyCapture,
  },
  props: {
    hotkeyName: {
      type: String,
      required: true,
      description: "The name of the OS-global hotkey to capture and update",
    },
  },
  setup(props) {
    const hotkey = ref<Hotkey | null>(null);

    const onUpdate = async (keys: Hotkey | null) => {
      if (!in_electron) {
        window.console.warn(
          "The hotkeys are only supported on desktop platforms"
        );
        return;
      }

      if (keys === null) {
        try {
          await hotkeys.ipc.unassign(props.hotkeyName);
          hotkey.value = null;
        } catch (error) {
          throw new Error("Error while clearing hotkey", { cause: error });
        }

        metrics.segment.track("Hotkey Cleared", {
          action: "cleared",
          name: props.hotkeyName,
        });
        return;
      }

      try {
        await hotkeys.ipc.assign(props.hotkeyName, keys);
        hotkey.value = keys;
      } catch {
        // Assignment failed (e.g the OS rejected the accelerator): fall back
        // to the default and reflect whatever ended up registered.
        await hotkeys.ipc.reset(props.hotkeyName);
        hotkey.value = await hotkeys.ipc.get(props.hotkeyName);
      }

      metrics.segment.track("Hotkey Mapped", {
        action: "mapped",
        name: props.hotkeyName,
        hotkey: hotkeys.format.stringify(
          keys,
          keymap.formatSymbol,
          keymap.separatorSpace
        ),
      });
    };

    onMounted(async () => {
      // Load the currently registered hotkey (desktop only).
      if (in_electron) {
        hotkey.value = await hotkeys.ipc.get(props.hotkeyName);
      }
    });

    return {
      hotkey,
      onUpdate,
    };
  },
});
</script>
